
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/components/AuthProvider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sanitizeInput } from "@/utils/security";
import { typecastSupabaseResult } from "@/utils/supabaseTypeUtils";
import { Profile } from "@/types/database";

// Use Zod for validation to ensure input safety
const notificationFormSchema = z.object({
  emailNotifications: z.boolean().default(true),
});

export function NotificationForm() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Ensure user ID is safely sanitized
  const safeUserId = user?.id ? sanitizeInput(user.id) : '';

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      if (!safeUserId) throw new Error("User ID is required");
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', safeUserId as any)
        .single();
      
      if (error) throw error;
      return typecastSupabaseResult<Profile>(data);
    },
    enabled: !!safeUserId,
  });

  const updateNotifications = useMutation({
    mutationFn: async (values: z.infer<typeof notificationFormSchema>) => {
      if (!safeUserId) throw new Error("User ID is required");
      
      const { error } = await supabase
        .from('profiles')
        .update({
          email_notifications: values.emailNotifications,
        } as any)
        .eq('id', safeUserId as any);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Notification preferences updated");
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      toast.error("Failed to update notification preferences");
      console.error('Error updating notifications:', error);
    },
  });

  const form = useForm<z.infer<typeof notificationFormSchema>>({
    resolver: zodResolver(notificationFormSchema),
    defaultValues: {
      emailNotifications: profile?.email_notifications ?? true,
    },
  });

  function onSubmit(values: z.infer<typeof notificationFormSchema>) {
    updateNotifications.mutate(values);
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="emailNotifications"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Email Notifications</FormLabel>
                <FormDescription>
                  Receive email notifications about your account activity.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full bg-black hover:bg-black/90">
          Save Preferences
        </Button>
      </form>
    </Form>
  );
}
